import { c as _c } from "react/compiler-runtime";
import { Checkbox, FormInput, FormSelect, Popover, PrimaryButton } from '@systemeio/ui';
import { QuestionMarkIcon, SettingsIcon } from '@systemeio/ui/icons';
import { FUNNELS_API, updateFunnel } from 'modules/funnels/api/funnels-api';
import { updateSaleSettings } from 'modules/funnels/api/sale-settings-api';
import { useOfferFormSettingsMutation } from 'modules/funnels/funnel/components/step-configuration-tab/offer-form-configuration-tab/hooks/use-offer-form-settings';
import { useFunnel } from 'modules/funnels/funnel/hooks/use-funnel';
import { useSaleSettings } from 'modules/funnels/funnel/hooks/use-sale-settings';
import { useFunnelStepsList } from 'modules/funnels/funnel/hooks/use-funnel-steps-list';
import { FunnelApiInterface, FunnelEditableErrorsInterface, FunnelEditableInterface, FunnelId, FunnelSettingsEditableInterface } from 'modules/funnels/types/funnel-interface';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import EditModal from 'shared/components/edit-modal';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useMutationUpdate } from 'shared/hooks/use-mutation-update';
import { usePaymentMethodsByCurrency } from 'shared/hooks/use-payment-methods-by-currency';
import { useUpdateData } from 'shared/hooks/use-update-data';
import useUser from 'shared/hooks/use-user';
import useUserDomains from 'shared/hooks/use-user-domains';
import { fetchWithFieldErrors } from 'shared/utils/fetch-with-errors';
import { getDomainInfoLink } from 'shared/utils/get-domain-info-link';
import { allowedPaymentMethodsFunnels } from '../constants/allowed-payment-methods';
interface EditFunnelModalState extends FunnelEditableInterface {
  disableTax: boolean;
}
const defaultErrors: FunnelEditableErrorsInterface = {
  fields: {
    name: '',
    domain: '',
    currency: '',
    paymentMethods: '',
    disableTax: ''
  }
};
const defaultValues: EditFunnelModalState = {
  name: '',
  domain: undefined,
  currency: undefined,
  paymentMethods: undefined,
  disableTax: false
};
function EditFunnelModal() {
  const $ = _c(70);
  const router = useRouter();
  const [errors, setErrors] = useState(defaultErrors);
  const [tempState, setTempState] = useState(defaultValues);
  const [opened, setOpened] = useState(false);
  const {
    t
  } = useLocoTranslation();
  const funnelId = parseInt(router.query.funnelId as string) as FunnelId;
  const {
    funnelStepsList
  } = useFunnelStepsList(funnelId);
  const {
    clearCurrencyRelatedFields
  } = useOfferFormSettingsMutation();
  const {
    user
  } = useUser();
  const {
    userDomainsSelect
  } = useUserDomains();
  const {
    funnel,
    mutateFunnel
  } = useFunnel(funnelId);
  const {
    saleSettings,
    mutateSaleSettings
  } = useSaleSettings(funnel?.saleSettingsId);
  const mutationUpdate = useMutationUpdate();
  let t0;
  if ($[0] !== t) {
    t0 = t("dashboard.funnel.currency_change_info");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = saleSettings?.currency;
  let t2;
  if ($[2] !== errors.fields || $[3] !== t0 || $[4] !== t1) {
    t2 = {
      currencyChangeMessage: t0,
      currency: t1,
      errors: errors.fields,
      allowedPaymentMethods: allowedPaymentMethodsFunnels
    };
    $[2] = errors.fields;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    currencyAndPaymentMethodsEditFields,
    isReady
  } = usePaymentMethodsByCurrency(t2);
  const t3 = `${FUNNELS_API}/${funnelId}`;
  const t4 = `${FUNNELS_API}/${funnelId}/steps/list`;
  const t5 = `${FUNNELS_API}/${funnelId}/steps/`;
  const t6 = `${FUNNELS_API}/${funnelId}/stats/`;
  const t7 = `${FUNNELS_API}/${funnelId}/leads/`;
  let t8;
  if ($[6] !== t4 || $[7] !== t5 || $[8] !== t6 || $[9] !== t7) {
    t8 = [`${FUNNELS_API}/list`, `${FUNNELS_API}/get-payment-methods`, `${FUNNELS_API}/step-remote-form`, t4, t5, t6, t7];
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t3 || $[12] !== t8) {
    t9 = {
      api: t3,
      constantApi: t8
    };
    $[11] = t3;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const updateData = useUpdateData(t9);
  let t10;
  if ($[14] !== clearCurrencyRelatedFields || $[15] !== funnel || $[16] !== funnelId || $[17] !== funnelStepsList || $[18] !== mutateFunnel || $[19] !== mutateSaleSettings || $[20] !== mutationUpdate || $[21] !== saleSettings?.currency || $[22] !== updateData) {
    t10 = async data => {
      await fetchWithFieldErrors(async () => {
        if (!funnel?.saleSettingsId) {
          return;
        }
        const newFunnelData = {
          name: data.name,
          domain: data.domain
        };
        const salesSettingsData = {
          currency: data.currency,
          paymentMethods: data.paymentMethods,
          disableTax: data.disableTax
        };
        const [funnelResponse, saleSettingsResponse] = await Promise.all([updateFunnel(Number(funnelId), newFunnelData), updateSaleSettings(funnel.saleSettingsId, salesSettingsData)]);
        await updateData(false, undefined, {
          mutationData: funnel,
          onMutate: async () => {
            await mutateFunnel(data_0 => {
              if (data_0) {
                return {
                  ...data_0,
                  name: funnelResponse.data.name,
                  domain: funnelResponse.data.domain
                };
              }
            }, false);
            await mutateSaleSettings(saleSettingsResponse.data, false);
          }
        }, {
          id: funnel?.id,
          onMutate: async apiKey => {
            await mutationUpdate(apiKey, data_1 => data_1 && {
              hasMore: data_1.hasMore,
              items: data_1.items.map(item => {
                if (item.id === funnelResponse.data.id) {
                  item = {
                    ...item,
                    name: funnelResponse.data.name
                  };
                }
                return item;
              })
            });
          }
        });
        if (saleSettings?.currency !== saleSettingsResponse.data.currency) {
          if (!funnelStepsList) {
            return;
          }
          await clearCurrencyRelatedFields(funnelStepsList.map(_temp));
        }
      }, setErrors);
    };
    $[14] = clearCurrencyRelatedFields;
    $[15] = funnel;
    $[16] = funnelId;
    $[17] = funnelStepsList;
    $[18] = mutateFunnel;
    $[19] = mutateSaleSettings;
    $[20] = mutationUpdate;
    $[21] = saleSettings?.currency;
    $[22] = updateData;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const onUpdateFunnel = t10;
  let t11;
  let t12;
  if ($[24] !== funnel) {
    t11 = () => {
      if (funnel) {
        setTempState(prev => ({
          ...prev,
          name: funnel.name,
          domain: funnel.domain.id
        }));
      }
    };
    t12 = [funnel];
    $[24] = funnel;
    $[25] = t11;
    $[26] = t12;
  } else {
    t11 = $[25];
    t12 = $[26];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[27] !== saleSettings) {
    t13 = () => {
      if (saleSettings) {
        setTempState(prev_0 => ({
          ...prev_0,
          currency: saleSettings.currency,
          paymentMethods: saleSettings.paymentMethods,
          disableTax: saleSettings.disableTax
        }));
      }
    };
    t14 = [saleSettings];
    $[27] = saleSettings;
    $[28] = t13;
    $[29] = t14;
  } else {
    t13 = $[28];
    t14 = $[29];
  }
  useEffect(t13, t14);
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setOpened(true);
    };
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = !funnel;
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SettingsIcon className="fill-background-secondary" />;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t) {
    t18 = t("dashboard.funnel.funnel_settings");
    $[32] = t;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t18) {
    t19 = <span className="hidden lg:flex">{t18}</span>;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t16 || $[37] !== t19) {
    t20 = <PrimaryButton onClick={t15} disabled={t16} data-test-element="funnel-settings-button">{t17}{t19}</PrimaryButton>;
    $[36] = t16;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t) {
    t21 = t("dashboard.funnel.edit_funnel");
    $[39] = t;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => {
      setOpened(false);
    };
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const t23 = !funnel || !saleSettings || !isReady;
  let t24;
  if ($[42] !== errors.fields || $[43] !== t) {
    t24 = {
      field: "name",
      onRender: (value, onChange, _, __, isPreFetching) => <FormInput key="funnel-edit-name" isPreFetching={isPreFetching} value={value} onChange={e => onChange(e.target.value)} label={t("global.name")} error={errors.fields.name} />
    };
    $[42] = errors.fields;
    $[43] = t;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== errors.fields || $[46] !== t || $[47] !== user?.dashboardLocale || $[48] !== userDomainsSelect) {
    t25 = {
      field: "domain",
      onRender: (value_0, onChange_0, __0, ___0, isPreFetching_0) => <FormSelect isPreFetching={isPreFetching_0} key="funnel-domain-edit" label={<div className="flex w-fit items-center gap-1">{t("dashboard.funnel.funnel_domain")}<Popover label={<TransLoco t={t} i18nKey="dashboard.funnel.domain_tooltip" components={{
          a: <a rel="noreferrer" target="_blank" className="primary-link w-full" href={getDomainInfoLink(user?.dashboardLocale)} />
        }} />} popoverClassName="w-[200px] sm:w-[350px]">{_temp2}</Popover></div>} data={userDomainsSelect} onChange={data_2 => onChange_0(data_2)} value={value_0} error={errors.fields.domain} withoutCloseIcon={true} />
    };
    $[45] = errors.fields;
    $[46] = t;
    $[47] = user?.dashboardLocale;
    $[48] = userDomainsSelect;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== errors.fields || $[51] !== t) {
    t26 = {
      field: "disableTax",
      onRender: (value_1, onChange_1, __1, ___1, isPreFetching_1) => <Checkbox key="funnel-edit-disable-tax" label={t("dashboard.funnel.disable_tax")} value={value_1} onChange={data_3 => onChange_1(data_3)} error={errors.fields.disableTax} isFetching={isPreFetching_1} />
    };
    $[50] = errors.fields;
    $[51] = t;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== currencyAndPaymentMethodsEditFields || $[54] !== t24 || $[55] !== t25 || $[56] !== t26) {
    t27 = [t24, t25, ...currencyAndPaymentMethodsEditFields, t26];
    $[53] = currencyAndPaymentMethodsEditFields;
    $[54] = t24;
    $[55] = t25;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== onUpdateFunnel) {
    t28 = data_4 => onUpdateFunnel(data_4);
    $[58] = onUpdateFunnel;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== opened || $[61] !== t21 || $[62] !== t23 || $[63] !== t27 || $[64] !== t28 || $[65] !== tempState) {
    t29 = <EditModal fullCaption={t21} opened={opened} onClose={t22} instance={tempState} isPreFetching={t23} editable={t27} onEdit={t28} />;
    $[60] = opened;
    $[61] = t21;
    $[62] = t23;
    $[63] = t27;
    $[64] = t28;
    $[65] = tempState;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t20 || $[68] !== t29) {
    t30 = <>{t20}{t29}</>;
    $[67] = t20;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  return t30;
}
function _temp2(show) {
  return <QuestionMarkIcon className={`${show ? "fill-brand-primary" : "fill-fill-primary"} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />;
}
function _temp(step) {
  return step.id;
}
export default EditFunnelModal;